// Транспорт и хуки отчётов руководителя (зеркало exec-панели, таблицы pex_*). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts + api/hooks.ts: там всё построено на эмуляции Bitrix
// REST (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/pex/*) со своими
// фильтрами. Смешивать их — значит однажды передать ListParams туда, где ждут строку запроса.
// То же решение, что у api/p73.ts.
//
// КАССЫ И ФОТ В ЭТОМ КОНТРАКТЕ НЕТ ВОВСЕ — и это не забывчивость. Ядро их не читает
// (см. `pex_api.py`): в `pex_fin_ops` лежат 4 462 выплаты людям и 257 ФИО (на 28.07.2026),
// а решения владельца о доступе пока нет. Поэтому здесь нет ни типа для операции кассы,
// ни хука за ней: чтобы добавить кассу на экран, придётся сначала открыть её в ядре,
// то есть принять решение осознанно, а не «дописать поле».

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/pex'

/** Тот же разбор ответа, что в api/client.ts и api/p73.ts: 401 → на логин, 400 → отказ. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица здесь читалась бы как
    // «переносов нет» и «долгов нет», поэтому поднимаем тот же тип, что и остальные разделы:
    // QueryState печатает по нему честное объяснение вместо пустого списка.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── общие части ответов ядра (см. pex_api.py) ───────────

/** Оговорки о данных. Ядро присылает их КАЖДЫМ ответом, и экран обязан их показывать. */
export type ОговоркиPex = Record<string, string>

export interface СтраницаPex<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  data_notes?: ОговоркиPex
}

// ─────────── переносы срока оплаты (pex_due_log) ───────────

export interface ПереносСтрока {
  payment_bitrix_id: number
  /** Сколько раз срок сдвигали. Больше двух — это «чинить», а не «звонить». */
  moves: number
  /** Первый известный срок и последний. «Известный» — журнал начат 08.07.2026. */
  due_first: string | null
  due_last: string | null
  moved_first: string | null
  moved_last: string | null
  /** Суммарно добавлено дней и разница между первым и последним сроком. */
  days_added: number | null
  days_shift: number | null
  /** Переносы, сделанные УЖЕ ПОСЛЕ наступления старого срока. Самый острый признак. */
  late_moves: number
  /** Переносы назад (срок приблизили). Бывают: 7 из 478 (на 28.07.2026). */
  back_moves: number
  /** false — платёж удалён в Bitrix после записи в журнал (6 из 404 на 28.07.2026). */
  payment_in_core: boolean
  payment_title: string | null
  stage_id: string | null
  category_id: number | null
  assigned_by_id: number | null
  amount: number | null
  currency: string
  /** Срок, который стоит в Bitrix сейчас. */
  due_now: string | null
  paid: boolean
  /** Бизнес-юнит платежа — СТРОКА-название (у сделки это числовой id enum). */
  unit: string | null
  deal_bitrix_id: number | null
  deal_title: string | null
  deal_closed: boolean | null
}

export interface СобытиеПереноса {
  evt_key: string
  payment_bitrix_id: number
  old_due: string | null
  new_due: string | null
  changed_at: string | null
  days_added: number | null
  moved_after_due: boolean | null
}

export interface ЖурналПлатежа {
  rows: СобытиеПереноса[]
  total: number
  /** null — платежа в ядре нет вовсе (удалён в Bitrix). Не пустой объект: это разное. */
  payment: {
    bitrix_id: number
    title: string | null
    stage_id: string | null
    assigned_by_id: number | null
    amount: number | null
    currency: string
    due_now: string | null
    paid: boolean
    unit: string | null
    deal_bitrix_id: number | null
  } | null
}

// ─────────── дневная динамика дебиторки (pex_recv_snapshots) ───────────

export interface ДеньДебиторки {
  snap_date: string
  total: number | null
  overdue: number | null
  pays: number | null
  units: number | null
  /** Долг без бизнес-юнита — отдельными колонками, потому что это 55 % суммы. */
  total_empty: number | null
  overdue_empty: number | null
}

export interface ТочкаРяда {
  d: string
  total: number | null
  overdue: number | null
  n: number | null
}

export interface ЮнитДебиторки {
  unit: string
  /** Сколько дней окна есть в данных. Меньше days_window — история с пропусками. */
  days: number
  days_window: number
  first_date: string | null
  last_date: string | null
  total: number | null
  overdue: number | null
  pays: number | null
  total_first: number | null
  overdue_first: number | null
  pays_first: number | null
  /** null — сравнивать не с чем (один день данных). НЕ ноль: ноль значил бы «не изменилось». */
  delta: number | null
  delta_overdue: number | null
  /** Сколько написаний юнита склеено по pex_dept_alias. 1 — синонимов не было. */
  merged_from: number
  /** true — это строка «бизнес-юнит не заполнен», а не настоящий юнит. */
  empty: boolean
  series: ТочкаРяда[]
}

export interface ОтветПоДням {
  rows: ДеньДебиторки[]
  total: number
  days_window: number
  empty_label: string
  data_notes: ОговоркиPex
}

export interface ОтветПоЮнитам {
  rows: ЮнитДебиторки[]
  total: number
  days_window: number
  empty_label: string
  data_notes: ОговоркиPex
}

// ─────────── возраст «косяков» (pex_kosyak_seen + reviews) ───────────

export interface КосякСтрока {
  seen_key: string
  chk: string
  deal_bitrix_id: number
  first_seen: string | null
  days: number | null
  /** true — first_seen это день начала журнала, то есть возраст «не меньше», а не «ровно». */
  age_censored: boolean
  reviewed: boolean
  reviewed_by: string | null
  reviewed_at: string | null
  deal_in_core: boolean
  deal_title: string | null
  stage_id: string | null
  category_id: number | null
  deal_closed: boolean | null
  assigned_by_id: number | null
  opportunity: number | null
  currency: string | null
  date_modify: string | null
  closedate: string | null
  /** Юнит сделки: id enum в unit_id, подпись в unit (её разворачивает ядро). */
  unit_id: string | null
  unit: string | null
}

export interface ВидКосяка {
  chk: string
  n: number
  first_seen_min: string | null
  first_seen_max: string | null
  days_min: number | null
  days_max: number | null
  days_median: number | null
  /** Сколько записей вида имеют обрезанный слева возраст. */
  censored: number
  reviewed: number
  /** Сколько сделок вида закрыты в ядре СЕЙЧАС — честный признак «уже неактуально». */
  deal_closed: number
  deal_missing: number
  /** Сумма только рублёвых сделок: курса в ядре нет. Это сумма СДЕЛОК, не ущерб. */
  amount_rub: number | null
  no_unit: number
}

export interface СводкаКосяков {
  rows: ВидКосяка[]
  total: number
  summary: {
    n: number
    deals: number
    reviewed: number
    deal_closed: number
    censored: number
    newest: string | null
    oldest: string | null
  }
  since: string
  data_notes: ОговоркиPex
}

// ─────────── эффективный руководитель отдела ───────────

export interface ОтделСтрока {
  dept_id: number
  dept_name: string | null
  parent_id: number | null
  sort: number | null
  head_bitrix: number | null
  head_bitrix_name: string | null
  head_override: number | null
  head_override_name: string | null
  override_set_by: string | null
  override_set_at: string | null
  head_effective: number | null
  head_effective_name: string | null
  /** 'bitrix' | 'override' | null — откуда взялся руководитель. */
  head_source: string | null
  /** Только действующие сотрудники: уволенные в составе отдела не числятся. */
  people: number
}

export interface КарточкаРуководителя {
  head_bitrix_id: number
  summary: {
    roots: number
    depts: number
    people: number
    kosyak: number
    kosyak_deals: number
    due_payments: number
  }
  depts: Array<{
    dept_id: number
    dept_name: string | null
    parent_id: number | null
    is_root: boolean
    people: number
  }>
  kosyak_by_check: Array<{
    chk: string
    n: number
    days_min: number | null
    days_max: number | null
    deal_closed: number
  }>
  due_payments: Array<{
    payment_bitrix_id: number
    moves: number
    due_last: string | null
    amount: number | null
    currency: string
    payment_title: string | null
    unit: string | null
    paid: boolean
    assigned_by_id: number | null
  }>
  /** Всегда 'people': по зоне ответственности это посчитать нечем, pex_dept_unit пуст. */
  measured_by: string
  data_notes: ОговоркиPex
}

// ─────────── справочники раздела ───────────

export interface СправочникиPex {
  units: Array<{
    unit: string
    /** Из каких написаний собран юнит. Больше одного — сработала канонизация. */
    raw_names: string[]
    days: number
    last_date: string | null
  }>
  checks: Record<string, number>
  alias: Array<{ raw: string; dept: string }>
  dept_map: Array<{ raw: string; dept: string }>
  counts: {
    due_log: number
    due_payments: number
    recv_rows: number
    kosyak: number
    kosyak_deals: number
    kosyak_reviews: number
    alias: number
    dept_map: number
    head_override: number
    depts: number
    depts_with_head: number
  }
  fresh: {
    /** Когда ядро читало панель. */
    due_log: string | null
    recv: string | null
    kosyak: string | null
    head_override: string | null
    /** Какой ДЕНЬ лежит в данных. Это другое число, и путать их нельзя. */
    snapshot_day: string | null
    snapshot_first_day: string | null
    kosyak_day: string | null
    due_last_event: string | null
    now: string | null
  }
  sync: Record<string, string | null>
  empty_label: string
  kosyak_since: string
  data_notes: ОговоркиPex
}

// ─────────── хуки ───────────
// staleTime большой: зеркало обновляется синком раз в 15–60 минут, а снимок дебиторки
// вообще суточный — чаще спрашивать нечего. placeholderData — чтобы таблица не мигала
// пустотой при смене фильтра (и чтобы «0 ₽» не мелькал там, где сумма ещё не доехала).

const общее = { retry: 1, staleTime: 60_000 } as const

export const usePexRefs = () =>
  useQuery({
    queryKey: ['pex', 'refs'],
    queryFn: () => запрос<СправочникиPex>('/refs'),
    staleTime: 5 * 60_000,
    retry: 1,
  })

export const useПереносы = (путь: string) =>
  useQuery({
    queryKey: ['pex', 'due-log', путь],
    queryFn: () => запрос<СтраницаPex<ПереносСтрока>>('/due-log' + путь),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useЖурналПлатежа = (id: number | string | undefined) =>
  useQuery({
    queryKey: ['pex', 'due-events', String(id ?? '')],
    queryFn: () => запрос<ЖурналПлатежа>(`/due-events?payment=${encodeURIComponent(String(id))}`),
    enabled: id !== undefined && id !== null && String(id) !== '',
    ...общее,
  })

export const useДебиторкаПоДням = (дней: number) =>
  useQuery({
    queryKey: ['pex', 'recv-days', дней],
    queryFn: () => запрос<ОтветПоДням>(`/recv-days?days=${дней}`),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useДебиторкаПоЮнитам = (дней: number) =>
  useQuery({
    queryKey: ['pex', 'recv-units', дней],
    queryFn: () => запрос<ОтветПоЮнитам>(`/recv-units?days=${дней}`),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useКосяки = (путь: string) =>
  useQuery({
    queryKey: ['pex', 'kosyak', путь],
    queryFn: () => запрос<СтраницаPex<КосякСтрока>>('/kosyak' + путь),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useСводкаКосяков = () =>
  useQuery({
    queryKey: ['pex', 'kosyak-summary'],
    queryFn: () => запрос<СводкаКосяков>('/kosyak-summary'),
    ...общее,
  })

export const useОтделы = (путь: string) =>
  useQuery({
    queryKey: ['pex', 'heads', путь],
    queryFn: () => запрос<СтраницаPex<ОтделСтрока>>('/heads' + путь),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useКарточкуРуководителя = (bx: number | string | undefined) =>
  useQuery({
    queryKey: ['pex', 'head', String(bx ?? '')],
    queryFn: () => запрос<КарточкаРуководителя>(`/head?bx=${encodeURIComponent(String(bx))}`),
    enabled: bx !== undefined && bx !== null && String(bx) !== '',
    ...общее,
  })
